import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '@/services/api';
import styles from './LiveSongRequestPanel.module.scss';

interface ScheduledSong {
  id: string;
  songDescription?: string;
  status?: string;
  sourceLink?: string;
  [key: string]: any;
}

interface SongRequest {
  id: string;
  songDescription?: string;
  status?: string;
  tableName?: string;
  displayName?: string;
  customerProfilePic?: string;
  customerName?: string;
  requestedByMe?: boolean;
  sourceLink?: string;
  [key: string]: any;
}

interface LiveSongAllRequestsProps {
  branchId: string;
}

const DEFAULT_LIMIT = 20;

function unwrapData<T>(res: { data?: T } | T): T | undefined {
  if (res == null) return undefined;
  if (typeof res === 'object' && 'data' in res && (res as { data?: T }).data !== undefined) return (res as { data: T }).data;
  return res as T;
}

export default function LiveSongAllRequests({ branchId }: LiveSongAllRequestsProps) {
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const [scheduledSongs, setScheduledSongs] = useState<ScheduledSong[]>([]);
  /** When API returns scheduledSongs as a number (count), we store it here for the dashboard. */
  const [scheduledCountFromApi, setScheduledCountFromApi] = useState<number | null>(null);
  const [myRequestPosition, setMyRequestPosition] = useState<number | undefined>();
  const [myRequestStatus, setMyRequestStatus] = useState<string | undefined>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getLiveSongAllRequests(branchId, { page, limit });
      const data = unwrapData(res);
      if (data) {
        setRequests(Array.isArray(data.requests) ? data.requests : []);
        const scheduled = data.scheduledSongs;
        if (Array.isArray(scheduled)) {
          setScheduledSongs(scheduled);
          setScheduledCountFromApi(null);
        } else {
          setScheduledSongs([]);
          setScheduledCountFromApi(typeof scheduled === 'number' ? scheduled : null);
        }
        setMyRequestPosition(data.myRequestPosition);
        setMyRequestStatus(data.myRequestStatus);
        setTotal(data.total ?? 0);
      } else {
        setRequests([]);
        setScheduledSongs([]);
        setScheduledCountFromApi(null);
        setTotal(0);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load requests');
      setRequests([]);
      setScheduledSongs([]);
      setScheduledCountFromApi(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [branchId, page, limit]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  if (loading && requests.length === 0 && scheduledSongs.length === 0) {
    return (
      <div className={`${styles.panel} ${styles.allRequestsSkeleton}`}>
        <div className={styles.skeletonDashboard}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonStat}>
              <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
              <div className={`${styles.skeletonLine} ${styles.skeletonLineTall}`} />
            </div>
          ))}
        </div>
        <div className={styles.skeletonList}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <div className={styles.skeletonAvatar} />
              <div className={styles.skeletonSongLine} style={{ maxWidth: i % 2 ? '70%' : '85%' }} />
              <div className={styles.skeletonBadge} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.panel}>
        <p className={styles.error} role="alert">{error}</p>
      </div>
    );
  }

  const scheduledCount = scheduledCountFromApi !== null ? scheduledCountFromApi : scheduledSongs.length;

  return (
    <div className={styles.panel}>
      {/* Dashboard: scheduled count, my position, my status */}
      <section className={styles.allRequestsDashboard}>
        <div className={styles.allRequestsStat}>
          <div className={styles.allRequestsStatLabel}>Scheduled in queue</div>
          <div className={styles.allRequestsStatValue}>{scheduledCount}</div>
        </div>
        <div className={styles.allRequestsStat}>
          <div className={styles.allRequestsStatLabel}>Your position</div>
          <div className={styles.allRequestsStatValue}>
            {myRequestPosition != null ? myRequestPosition : '—'}
          </div>
        </div>
        <div className={styles.allRequestsStat}>
          <div className={styles.allRequestsStatLabel}>Your status</div>
          <div className={styles.allRequestsStatValue}>
            {myRequestStatus ? String(myRequestStatus) : '—'}
          </div>
        </div>
      </section>

      {/* Scheduled queue (now playing + up next) */}
      {scheduledSongs.length > 0 && (
        <section className={styles.listSection}>
          <h3 className={styles.listTitle}>Queue</h3>
          <ul className={styles.requestList}>
            {scheduledSongs.map((song, index) => {
              const isCurrent = (song.status || '').toLowerCase() === 'current';
              const label = isCurrent ? 'Now playing' : `#${index + 1}`;
              return (
                <li key={song.id} className={styles.requestItem}>
                  <div className={styles.requestSummaryRow}>
                    <div className={styles.requestSummary} style={{ cursor: 'default' }}>
                      <span className={styles.requestSerial}>{label}</span>
                      <span className={styles.requestSong}>{song.songDescription || 'Song'}</span>
                      <span className={styles.requestStatus} data-status={song.status || 'scheduled'}>
                        {isCurrent ? 'Playing' : (song.status || 'Scheduled')}
                      </span>
                    </div>
                  </div>
                  {song.sourceLink && (
                    <div className={styles.requestDetail}>
                      <p className={styles.requestMeta}>
                        <a href={song.sourceLink} target="_blank" rel="noopener noreferrer">Link</a>
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* All requests (paginated) */}
      <section className={styles.listSection}>
        <div className={styles.listSectionHeader}>
          <h3 className={styles.listTitle}>All requests</h3>
        </div>
        <div className={styles.listOnPage}>
          {requests.length === 0 ? (
            <p className={styles.requestMeta}>No requests on this page.</p>
          ) : (
            <ul className={styles.requestList}>
              {requests.map((req, index) => {
                const requestedByMe = req.requestedByMe === true;
                const name = req.customerName || req.displayName;
                return (
                  <li
                    key={req.id}
                    className={`${styles.requestItem} ${requestedByMe ? styles.requestItemRequestedByMe : ''}`}
                  >
                    <div className={styles.requestSummaryRow}>
                      <div className={styles.requestSummary} style={{ cursor: 'default' }}>
                        {req.customerProfilePic ? (
                          <img
                            src={req.customerProfilePic}
                            alt=""
                            className={styles.requestAvatar}
                            loading="lazy"
                          />
                        ) : (
                          <div className={styles.requestAvatarFallback} aria-hidden>
                            {(name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={styles.requestSerial}>{(page - 1) * limit + index + 1}.</span>
                        <span className={styles.requestSong}>{req.songDescription || 'Song request'}</span>
                        {requestedByMe && (
                          <span className={styles.requestStatus} data-status="scheduled">You</span>
                        )}
                        <span className={styles.requestStatus} data-status={req.status}>
                          {req.status || '—'}
                        </span>
                      </div>
                    </div>
                    {(req.tableName || name || req.sourceLink) && (
                      <div className={styles.requestDetail}>
                        <p className={styles.requestMeta}>
                          {req.tableName && <>Table: {req.tableName}</>}
                          {req.tableName && name && ' · '}
                          {name && <>Requested by: {name}</>}
                          {req.sourceLink && (
                            <>
                              {(req.tableName || name) && ' · '}
                              <a href={req.sourceLink} target="_blank" rel="noopener noreferrer">Link</a>
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            {total > 0
              ? `Page ${page} of ${totalPages} (${total} total)`
              : 'No requests'}
          </span>
          <div className={styles.paginationButtons}>
            <button
              type="button"
              className={styles.paginationBtn}
              disabled={!hasPrev || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className={styles.paginationBtn}
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
